// Client for the separate TRMD Yamaha Engine (ws://localhost:8766) — J2534 only.
import { BridgeClient, probeBridge } from "./bridge";

export const YAMAHA_ENGINE_URL = "ws://localhost:8766";

export const YAMAHA_IFACES = [
  { id: "sm3", label: "Scanmatik SM3 USB" },
  { id: "sm3wifi", label: "Scanmatik SM3 Wi-Fi (manual — memutus internet)" },
  { id: "sm2", label: "Scanmatik SM2" },
  { id: "tactrix", label: "Tactrix Openport 2.0" },
];

// Index == ECU type order of the reference flashtool.
export const YAMAHA_ECUS = [
  { label: "MC9S12T64 · 64KB · K-Line (raw 9700)", bus: "raw", sizes: [59392] },
  { label: "MC9S12XS128 · 128KB · K-Line (raw 9700)", bus: "raw", sizes: [114688] },
  { label: "MC9S12XS128 New · 128KB · K-Line (raw 9700)", bus: "raw", sizes: [114688] },
  { label: "MC9S12XS256 · 256KB · K-Line", bus: "kline", sizes: [245760] },
  { label: "M16C R5F35633 · 96KB · K-Line", bus: "kline", sizes: [32768] },
  { label: "M16C R5F35636 · 128KB · K-Line", bus: "kline", sizes: [65536] },
  { label: "M16C R5F3563E · 256KB · K-Line", bus: "kline", sizes: [65536, 20480] },
  { label: "R8C R5F2154CG · 128KB · K-Line", bus: "kline", sizes: [16384] },
  { label: "R8C R5F2156CG · 128KB · K-Line", bus: "kline", sizes: [24576] },
  { label: "RL78 R5F10ALG · 128KB · K-Line", bus: "kline", sizes: [102400] },
  { label: "RL78 R5F10PLH · 192KB · K-Line", bus: "kline", sizes: [27648, 156672] },
  { label: "RL78 R5F113LK · 384KB · CAN", bus: "can", sizes: [60416, 107520, 260096, 307200] },
  { label: "SH7058 Denso · 1024KB · CAN", bus: "can", sizes: [917504] },
  { label: "RH850 R7F701029 · 1024KB · CAN", bus: "can", sizes: [720896] },
  { label: "RH850 R7F701201 · 2048KB · CAN", bus: "can", sizes: [2031616] },
];

const hasSig = (bytes, sig) => {
  outer: for (let i = 0; i <= bytes.length - sig.length; i++) {
    for (let j = 0; j < sig.length; j++) if (bytes[i + j] !== sig[j]) continue outer;
    return true;
  }
  return false;
};
const SIG_XS128N = Array.from("MC9S12XS128N").map((c) => c.charCodeAt(0));
const SIG_35636 = [180, 229, 172, 173, 33, 26, 187, 223];

// Auto-pick the ECU type from BIN size (same rule as the reference tool).
export function ecuFromBin(bytes) {
  const n = bytes.length;
  if (n === 59392) return 0;
  if (n === 114688) return hasSig(bytes, SIG_XS128N) ? 2 : 1;
  if (n === 245760) return 3;
  if (n === 32768) return 4;
  if (n === 65536) return hasSig(bytes, SIG_35636) ? 5 : 6;
  if (n === 16384) return 7;
  if (n === 24576) return 8;
  if (n === 102400) return 9;
  if (n === 27648 || n === 156672) return 10;
  if ([60416, 107520, 260096, 307200].includes(n)) return 11;
  if (n === 917504) return 12;
  if (n === 720896) return 13;
  if (n === 2031616) return 14;
  return -1;
}

const toHex = (u8) => Array.from(u8, (b) => b.toString(16).padStart(2, "0")).join("");

// Protokol transport: K-Line (termasuk raw 9700) dan CAN.
export const YAMAHA_PROTOS = [
  { id: "kline", label: "K-LINE" },
  { id: "can", label: "CANBUS" },
];

export const ecuProto = (e) => (e && e.bus === "can" ? "can" : "kline");

export const ecuIdxByProto = (p) =>
  YAMAHA_ECUS.map((e, i) => i).filter((i) => ecuProto(YAMAHA_ECUS[i]) === p);

// Hardware code hasil READ INFO → tipe ECU (device-confirmed).
export const HW_TO_ECU = {
  "05120001": 4,
  "05120002": 4,
  "05120003": 4,
  "128A6001": 11,
  // 15 Jun 2026: READ INFO sukses penuh di tipe 3 (1A 94 -> 120D6000, Part B3F-H594A-00,
  // vendor DEN., reprog flag 0000). Device-confirmed.
  "120D6000": 3,
  // 19 Jun 2026: user device — Software Id 12688000, Part BJ8-H591A-00 (BJ80/BJ81) = M16C R5F35633 96KB.
  "12688000": 4,
};

// Kandidat probe AUTO DETECT per protokol (read-only, pakai READ INFO).
export const AUTO_CANDIDATES = { kline: [3, 4, 5, 9, 10], can: [11, 13] };

// Ukuran dump PENUH per tipe ECU (hasil baca chip) vs ukuran payload yang ditulis tool asli.
// Dipakai HANYA untuk pesan informasi — JANGAN dipakai untuk auto-pilih tipe ECU, karena dump
// penuh tidak boleh ditulis apa adanya (area bootloader/vector di ujung tidak ikut ditulis).
export const FULL_DUMP_SIZES = {
  262144: { ecu: 3, payload: 245760, note: "dump penuh 256 KB; tool asli hanya menulis 245.760 B pertama (0x00000–0x3BFFF). 16 KB terakhir = bootloader + vector table." },
};


export async function pingYamahaEngine() {
  return probeBridge(YAMAHA_ENGINE_URL, 6000);
}

async function withEngine(fn) {
  const c = new BridgeClient(YAMAHA_ENGINE_URL);
  await c.connect();
  try { return await fn(c); } finally { try { c.ws && c.ws.close(); } catch (_) {} }
}

export const yamahaInfo = (dll, ecu, onProgress) => withEngine((c) => c._rpc({ cmd: "yamahaInfo", dll, ecu }, 150000, onProgress));
export const yamahaCrc = (dll, ecu, size, onProgress) => withEngine((c) => c._rpc({ cmd: "yamahaCrc", dll, ecu, size }, 420000, onProgress));
export const yamahaLive = (dll, ecu, rounds, pid, onProgress) => withEngine((c) => c._rpc({ cmd: "yamahaLive", dll, ecu, rounds, pid }, 300000, onProgress));
export const yamahaScanPids = (dll, ecu, onProgress) => withEngine((c) => c._rpc({ cmd: "yamahaScanPids", dll, ecu, from: 0, to: 255 }, 900000, onProgress));
export const yamahaDevices = () => withEngine((c) => c._rpc({ cmd: "yamahaDevices" }, 20000));
export const yamahaYmeter = (dll, rounds, onProgress) => withEngine((c) => c._rpc({ cmd: "yamahaYmeter", dll, rounds }, 300000, onProgress));
export const yamahaClearDtc = (dll, ecu, onProgress) => withEngine((c) => c._rpc({ cmd: "yamahaClearDtc", dll, ecu }, 150000, onProgress));
export const yamahaProbeRead = (dll, ecu, onProgress) => withEngine((c) => c._rpc({ cmd: "yamahaProbeRead", dll, ecu }, 420000, onProgress));
export const yamahaReadCan = (dll, ecu, size, onProgress) => withEngine((c) => c._rpc({ cmd: "yamahaReadCan", dll, ecu, size }, 2700000, onProgress));
export const yamahaScanBus = (dll, onProgress) => withEngine((c) => c._rpc({ cmd: "yamahaScanBus", dll }, 300000, onProgress));
export const yamahaRecover = (dll, onProgress) => withEngine((c) => c._rpc({ cmd: "yamahaRecover", dll }, 180000, onProgress));
// Peta verifikasi read-only: rutin 31 02 per sub-rentang, untuk mencari DI MANA flash berbeda dari file.
export const yamahaVerifyMap = (dll, ecu, chunks, onProgress) => withEngine((c) => c._rpc({ cmd: "yamahaVerifyMap", dll, ecu, chunks }, 900000, onProgress));
// READ BACK read-only: baca isi flash sebenarnya (service 23 / 35+36) untuk membandingkan byte per byte.
export const yamahaReadBack = (dll, ecu, addr, len, onProgress) => withEngine((c) => c._rpc({ cmd: "yamahaReadBack", dll, ecu, addr, len }, 300000, onProgress));
export const yamahaFinalize = (dll, ecu, onProgress) => withEngine((c) => c._rpc({ cmd: "yamahaFinalize", dll, ecu }, 300000, onProgress));
// Pulihkan reprog flag ke 0000 saja (tanpa menulis serial/tanggal) — untuk flag tersangkut spt 5AA5.
export const yamahaResetFlag = (dll, ecu, onProgress) => withEngine((c) => c._rpc({ cmd: "yamahaFinalize", dll, ecu, flagOnly: 1 }, 300000, onProgress));
export const yamahaWrite = (dll, ecu, bytes, onProgress) =>
  withEngine((c) => c._rpc({ cmd: "yamahaWrite", dll, ecu, arm: 1, data: toHex(bytes) }, 5400000, onProgress));
